import { Location } from 'history'
import { ReactElement, useEffect, useState } from 'react'
import { Outlet, useLocation } from 'react-router'
import { useParams } from 'react-router-dom'
import { ROUTES } from 'src/routing/routes'
import { NestedRoute, Route, SecondLevelRoute } from 'src/routing/routing'
import { SecondLevelNavigation, SecondLevelNavigationContainer } from 'src/routing/SecondLevelNavigation'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { NotAuthorized } from 'src/shared/not-authorized/NotAuthorized'
import {
  usePermissionsForFactsheetDetails,
  usePermissionsForFactsheetDetailsSummaryPage,
} from 'src/service/security/PermissionHook'

const resolveActiveRoute = (location: Location, factsheetId: string): SecondLevelRoute => {
  const nested = ROUTES.FactsheetDescriptionRoot!.nested! as unknown as NestedRoute

  const activeRoute = Object.keys(nested).find(
    (key) =>
      location.pathname.endsWith(nested[key].path) ||
      (ROUTES.FactsheetDescriptionRoot.params({ factsheetId }) === location.pathname && nested[key].primary),
  ) as string
  return nested[activeRoute] as SecondLevelRoute
}

export const FactsheetDescriptionIndexPage = (): ReactElement => {
  const { factsheetId } = useParams()
  const location = useLocation()

  const { loading, canView } = usePermissionsForFactsheetDetails(parseInt(factsheetId as string))
  const { canView: canViewSummary } = usePermissionsForFactsheetDetailsSummaryPage(parseInt(factsheetId as string))

  const [activeRoute, setActiveRoute] = useState<SecondLevelRoute>(() => {
    return resolveActiveRoute(location, factsheetId as string)
  })

  useEffect(() => {
    const activeRoute = resolveActiveRoute(location, factsheetId as string)
    setActiveRoute(activeRoute)
  }, [location, factsheetId])

  const showNavigation = activeRoute && !activeRoute.hideNavigation

  const isNestedRouteAllowedAdditionalValidation = (nestedRoute: SecondLevelRoute): boolean => {
    switch (nestedRoute) {
      case ROUTES.FactsheetDescriptionRoot.nested.FactsheetSummary: {
        return canViewSummary
      }

      default:
        return true
    }
  }

  if (!loading && !canView) {
    return <NotAuthorized />
  }

  return (
    <>
      {!loading && (
        <SecondLevelNavigationContainer $showNavigation={showNavigation}>
          {showNavigation && (
            <PageLayout>
              <SecondLevelNavigation
                activeRoute={activeRoute}
                route={ROUTES.FactsheetDescriptionRoot as unknown as Route}
                baseUrl={ROUTES.FactsheetDescriptionRoot.params({ factsheetId })}
                isNestedRouteAllowed={isNestedRouteAllowedAdditionalValidation}
              />
            </PageLayout>
          )}
          <Outlet />
        </SecondLevelNavigationContainer>
      )}
    </>
  )
}
